import React from 'react';
import { Container } from '../components/ui/Container';
export function TermsPage() {
  return <div className="bg-white py-20 lg:py-28" data-id="element-2234">
      <Container size="md" data-id="element-2235">
        <h1 className="text-4xl font-bold tracking-tight text-gray-900 sm:text-5xl mb-8" data-id="element-2236">
          Terms of Service
        </h1>
        <p className="text-gray-500 mb-12" data-id="element-2237">Last updated: December 15, 2024</p>

        <div className="prose prose-lg prose-indigo max-w-none" data-id="element-2238">
          <p className="text-xl text-gray-600 mb-8" data-id="element-2239">
            These Terms of Service ("Terms") govern your access to and use of
            Flowsense ("Service"), operated by Acciom Limited ("Company", "we",
            "us", or "our"). By accessing or using the Service, you agree to be
            bound by these Terms.
          </p>

          <div className="bg-yellow-50 border-l-4 border-yellow-500 p-6 mb-8" data-id="element-2240">
            <p className="text-sm text-yellow-900 font-semibold mb-2" data-id="element-2241">
              IMPORTANT: PLEASE READ CAREFULLY
            </p>
            <p className="text-sm text-yellow-800 mb-0" data-id="element-2242">
              These Terms contain important information about your rights and
              obligations, including mandatory arbitration and class action
              waiver provisions. If you do not agree to these Terms, do not use
              the Service.
            </p>
          </div>

          <h2 data-id="element-2243">1. Acceptance of Terms</h2>

          <h3 data-id="element-2244">1.1 Agreement to Terms</h3>
          <p data-id="element-2245">
            By creating an account, accessing, or using Flowsense, you
            acknowledge that you have read, understood, and agree to be bound by
            these Terms and our Privacy Policy. If you are using the Service on
            behalf of an organization, you represent and warrant that you have
            the authority to bind that organization to these Terms.
          </p>

          <h3 data-id="element-2246">1.2 Eligibility</h3>
          <p data-id="element-2247">
            You must be at least 18 years old and have the legal capacity to
            enter into contracts to use the Service. By using the Service, you
            represent and warrant that you meet these requirements.
          </p>

          <h3 data-id="element-2248">1.3 Changes to Terms</h3>
          <p data-id="element-2249">
            We reserve the right to modify these Terms at any time. We will
            provide notice of material changes by:
          </p>
          <ul data-id="element-2250">
            <li data-id="element-2251">
              Posting the updated Terms on this page with a new "Last updated"
              date
            </li>
            <li data-id="element-2252">
              Sending an email notification to your registered email address at
              least 30 days before changes take effect
            </li>
            <li data-id="element-2253">Displaying an in-app notification</li>
          </ul>
          <p data-id="element-2254">
            Your continued use of the Service after changes take effect
            constitutes acceptance of the modified Terms. If you do not agree to
            the changes, you must stop using the Service and may cancel your
            account.
          </p>

          <h2 data-id="element-2255">2. Description of Service</h2>

          <h3 data-id="element-2256">2.1 What Flowsense Does</h3>
          <p data-id="element-2257">
            Flowsense is a SaaS platform that connects to your Klaviyo account
            via OAuth to analyze your email marketing flows and provide
            AI-powered optimization recommendations. The Service includes:
          </p>
          <ul data-id="element-2258">
            <li data-id="element-2259">Automated flow analysis and health scoring</li>
            <li data-id="element-2260">AI-generated recommendations for improving flow performance</li>
            <li data-id="element-2261">Performance dashboards and reporting</li>
            <li data-id="element-2262">Multi-client management for agencies</li>
            <li data-id="element-2263">White-label reporting capabilities (on applicable plans)</li>
          </ul>

          <h3 data-id="element-2264">2.2 Service Availability</h3>
          <p data-id="element-2265">
            We strive to provide 99.9% uptime, but the Service is provided "as
            available" and "as is." We do not guarantee uninterrupted access and
            may suspend or terminate the Service for maintenance, updates, or
            unforeseen circumstances. We will provide advance notice of
            scheduled maintenance when possible.
          </p>

          <h3 data-id="element-2266">2.3 Service Modifications</h3>
          <p data-id="element-2267">
            We reserve the right to modify, suspend, or discontinue any aspect
            of the Service at any time, with or without notice. We will not be
            liable to you or any third party for any modification, suspension,
            or discontinuation of the Service.
          </p>

          <h2 data-id="element-2268">3. User Accounts</h2>

          <h3 data-id="element-2269">3.1 Account Registration</h3>
          <p data-id="element-2270">
            To use the Service, you must create an account by providing
            accurate, current, and complete information, including:
          </p>
          <ul data-id="element-2271">
            <li data-id="element-2272">Full name</li>
            <li data-id="element-2273">Valid email address</li>
            <li data-id="element-2274">Secure password</li>
            <li data-id="element-2275">Company name (optional)</li>
          </ul>
          <p data-id="element-2276">
            You agree to keep your account information up to date and accurate
            at all times.
          </p>

          <h3 data-id="element-2277">3.2 Account Security</h3>
          <p data-id="element-2278">
            You are responsible for maintaining the confidentiality of your
            account credentials and for all activities that occur under your
            account. You agree to:
          </p>
          <ul data-id="element-2279">
            <li data-id="element-2280">Use a strong, unique password</li>
            <li data-id="element-2281">Enable two-factor authentication (2FA) when available</li>
            <li data-id="element-2282">Not share your account credentials with anyone</li>
            <li data-id="element-2283">
              Notify us immediately of any unauthorized access or security
              breach
            </li>
          </ul>
          <p data-id="element-2284">
            We are not liable for any loss or damage arising from your failure
            to protect your account credentials.
          </p>

          <h3 data-id="element-2285">3.3 Account Types</h3>
          <p data-id="element-2286">
            Flowsense offers different account types with varying features and
            limitations:
          </p>
          <ul data-id="element-2287">
            <li data-id="element-2288">
              <strong data-id="element-2289">Free Plan:</strong> Limited features, 1 client, basic
              support
            </li>
            <li data-id="element-2290">
              <strong data-id="element-2291">Pro Plan:</strong> Full features, unlimited clients,
              priority support
            </li>
            <li data-id="element-2292">
              <strong data-id="element-2293">Agency Plan:</strong> White-label features, team
              collaboration, dedicated support
            </li>
          </ul>
          <p data-id="element-2294">
            Plan details and pricing are available at{' '}
            <a href="https://useflowsense.com/pricing" data-id="element-2295">
              useflowsense.com/pricing
            </a>
            .
          </p>

          <h2 data-id="element-2296">4. Klaviyo Integration</h2>

          <h3 data-id="element-2297">4.1 OAuth Authorization</h3>
          <p data-id="element-2298">
            To use the Service, you must connect your Klaviyo account via OAuth
            2.0. By authorizing the connection, you grant Flowsense read-only
            access to:
          </p>
          <ul data-id="element-2299">
            <li data-id="element-2300">Flow structure and configuration</li>
            <li data-id="element-2301">Email templates and content</li>
            <li data-id="element-2302">Aggregate performance metrics (opens, clicks, revenue)</li>
            <li data-id="element-2303">Event data for trigger analysis</li>
          </ul>

          <h3 data-id="element-2304">4.2 Data Access Limitations</h3>
          <p data-id="element-2305">
            Flowsense only requests and accesses the minimum data necessary to
            provide the Service. We do not:
          </p>
          <ul data-id="element-2306">
            <li data-id="element-2307">
              Access individual subscriber email addresses, names, or personal
              information
            </li>
            <li data-id="element-2308">Modify, delete, or send emails on your behalf</li>
            <li data-id="element-2309">Access payment information or financial data</li>
            <li data-id="element-2310">
              Share your Klaviyo data with other users or third parties (except
              as required to provide the Service)
            </li>
          </ul>

          <h3 data-id="element-2311">4.3 Revoking Access</h3>
          <p data-id="element-2312">
            You may revoke Flowsense's access to your Klaviyo account at any
            time by:
          </p>
          <ul data-id="element-2313">
            <li data-id="element-2314">
              Disconnecting the integration in your Flowsense account settings
            </li>
            <li data-id="element-2315">Revoking the OAuth token in your Klaviyo account settings</li>
          </ul>
          <p data-id="element-2316">
            Revoking access will stop data syncing but will not delete
            historical data already stored in Flowsense. To delete historical
            data, you must delete your Flowsense account.
          </p>

          <h2 data-id="element-2317">5. Payment Terms</h2>

          <h3 data-id="element-2318">5.1 Subscription Plans</h3>
          <p data-id="element-2319">
            Flowsense offers subscription plans billed monthly or annually. By
            subscribing to a paid plan, you agree to pay all applicable fees.
          </p>

          <h3 data-id="element-2320">5.2 Billing</h3>
          <ul data-id="element-2321">
            <li data-id="element-2322">
              <strong data-id="element-2323">Payment Method:</strong> You must provide a valid payment
              method (credit card or debit card) processed securely through
              Stripe
            </li>
            <li data-id="element-2324">
              <strong data-id="element-2325">Automatic Renewal:</strong> Subscriptions automatically
              renew at the end of each billing period unless canceled
            </li>
            <li data-id="element-2326">
              <strong data-id="element-2327">Billing Date:</strong> You will be charged on the same day
              each billing period
            </li>
            <li data-id="element-2328">
              <strong data-id="element-2329">Failed Payments:</strong> If payment fails, we will
              attempt to charge your payment method up to 3 times. If all
              attempts fail, your account may be suspended or downgraded to the
              free plan
            </li>
          </ul>

          <h3 data-id="element-2330">5.3 Pricing Changes</h3>
          <p data-id="element-2331">
            We may change our pricing at any time. Price changes will not affect
            your current billing period but will apply to subsequent renewals.
            We will provide at least 30 days' notice of price increases.
          </p>

          <h3 data-id="element-2332">5.4 Taxes</h3>
          <p data-id="element-2333">
            All fees are exclusive of applicable taxes (VAT, sales tax, etc.).
            You are responsible for paying all taxes associated with your use of
            the Service.
          </p>

          <h3 data-id="element-2334">5.5 Refunds</h3>
          <p data-id="element-2335">
            We offer a 14-day money-back guarantee for new paid subscriptions.
            To request a refund, contact us at{' '}
            <a href="mailto:support@useflowsense.com" data-id="element-2336">
              support@useflowsense.com
            </a>{' '}
            within 14 days of your initial purchase. Refunds are not available
            for renewals or after the 14-day period.
          </p>

          <h3 data-id="element-2337">5.6 Cancellation</h3>
          <p data-id="element-2338">
            You may cancel your subscription at any time from your account
            settings. Cancellation takes effect at the end of your current
            billing period. You will retain access to paid features until the
            end of the billing period. No refunds are provided for partial
            billing periods.
          </p>

          <h2 data-id="element-2339">6. Acceptable Use Policy</h2>

          <h3 data-id="element-2340">6.1 Prohibited Activities</h3>
          <p data-id="element-2341">You agree not to use the Service to:</p>
          <ul data-id="element-2342">
            <li data-id="element-2343">
              Violate any applicable laws, regulations, or third-party rights
            </li>
            <li data-id="element-2344">Transmit malware, viruses, or malicious code</li>
            <li data-id="element-2345">
              Attempt to gain unauthorized access to the Service or other users'
              accounts
            </li>
            <li data-id="element-2346">Reverse engineer, decompile, or disassemble the Service</li>
            <li data-id="element-2347">Use automated scripts or bots to access the Service</li>
            <li data-id="element-2348">Scrape, crawl, or harvest data from the Service</li>
            <li data-id="element-2349">Interfere with or disrupt the Service or servers</li>
            <li data-id="element-2350">Impersonate another person or entity</li>
            <li data-id="element-2351">
              Use the Service for competitive analysis or to build a competing
              product
            </li>
            <li data-id="element-2352">Resell or redistribute the Service without authorization</li>
          </ul>

          <h3 data-id="element-2353">6.2 Consequences of Violations</h3>
          <p data-id="element-2354">Violation of this Acceptable Use Policy may result in:</p>
          <ul data-id="element-2355">
            <li data-id="element-2356">Immediate suspension or termination of your account</li>
            <li data-id="element-2357">Removal of content or data</li>
            <li data-id="element-2358">Legal action and liability for damages</li>
            <li data-id="element-2359">Reporting to law enforcement authorities</li>
          </ul>

          <h2 data-id="element-2360">7. Intellectual Property</h2>

          <h3 data-id="element-2361">7.1 Flowsense Ownership</h3>
          <p data-id="element-2362">
            The Service, including all software, algorithms, designs, text,
            graphics, logos, and other content, is owned by Acciom Limited and
            protected by copyright, trademark, patent, and other intellectual
            property laws. You may not copy, modify, distribute, sell, or lease
            any part of the Service without our express written permission.
          </p>

          <h3 data-id="element-2363">7.2 Your Content</h3>
          <p data-id="element-2364">
            You retain all ownership rights to the data you provide to
            Flowsense, including:
          </p>
          <ul data-id="element-2365">
            <li data-id="element-2366">Client information you enter</li>
            <li data-id="element-2367">Notes and annotations you add</li>
            <li data-id="element-2368">Custom reports you create</li>
          </ul>
          <p data-id="element-2369">
            By using the Service, you grant us a limited, non-exclusive,
            royalty-free license to use, store, and process your content solely
            to provide and improve the Service.
          </p>

          <h3 data-id="element-2370">7.3 Klaviyo Data</h3>
          <p data-id="element-2371">
            Data synced from your Klaviyo account remains your property. We use
            this data only to provide the Service and generate recommendations.
            We do not claim ownership of your Klaviyo data.
          </p>

          <h3 data-id="element-2372">7.4 AI-Generated Recommendations</h3>
          <p data-id="element-2373">
            Recommendations generated by our AI models are provided for
            informational purposes only. While we strive for accuracy, we do not
            guarantee the effectiveness of any recommendation. You are solely
            responsible for implementing recommendations and their outcomes.
          </p>

          <h3 data-id="element-2374">7.5 Feedback</h3>
          <p data-id="element-2375">
            If you provide feedback, suggestions, or ideas about the Service,
            you grant us a perpetual, irrevocable, worldwide, royalty-free
            license to use, modify, and incorporate that feedback without
            compensation or attribution.
          </p>

          <h2 data-id="element-2376">8. Data Privacy and Security</h2>

          <h3 data-id="element-2377">8.1 Privacy Policy</h3>
          <p data-id="element-2378">
            Our collection, use, and protection of your data is governed by our
            Privacy Policy, which is incorporated into these Terms by reference.
            Please review the Privacy Policy at{' '}
            <a href="https://useflowsense.com/privacy" data-id="element-2379">
              useflowsense.com/privacy
            </a>
            .
          </p>

          <h3 data-id="element-2380">8.2 Data Security</h3>
          <p data-id="element-2381">
            We implement industry-standard security measures to protect your
            data, including encryption, access controls, and regular security
            audits. However, no system is 100% secure. You acknowledge that you
            use the Service at your own risk.
          </p>

          <h3 data-id="element-2382">8.3 Data Breach Notification</h3>
          <p data-id="element-2383">
            In the event of a data breach that affects your account, we will
            notify you via email within 72 hours of discovering the breach and
            provide information about the nature of the breach and steps we are
            taking to address it.
          </p>

          <h2 data-id="element-2384">9. Third-Party Services</h2>

          <h3 data-id="element-2385">9.1 Klaviyo</h3>
          <p data-id="element-2386">
            Flowsense integrates with Klaviyo via their API. Your use of Klaviyo
            is subject to Klaviyo's Terms of Service and Privacy Policy. We are
            not responsible for Klaviyo's service availability, data practices,
            or any issues arising from your Klaviyo account.
          </p>

          <h3 data-id="element-2387">9.2 Other Integrations</h3>
          <p data-id="element-2388">
            The Service may integrate with other third-party services (e.g.,
            Stripe for payments). Your use of these services is subject to their
            respective terms and privacy policies. We are not responsible for
            the practices or content of third-party services.
          </p>

          <h3 data-id="element-2389">9.3 Third-Party Links</h3>
          <p data-id="element-2390">
            The Service may contain links to third-party websites. We do not
            endorse or control these websites and are not responsible for their
            content or practices.
          </p>

          <h2 data-id="element-2391">10. Warranties and Disclaimers</h2>

          <h3 data-id="element-2392">10.1 Service Warranty Disclaimer</h3>
          <p data-id="element-2393">
            THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT
            WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT
            LIMITED TO:
          </p>
          <ul data-id="element-2394">
            <li data-id="element-2395">
              Warranties of merchantability, fitness for a particular purpose,
              or non-infringement
            </li>
            <li data-id="element-2396">
              Warranties that the Service will be uninterrupted, error-free, or
              secure
            </li>
            <li data-id="element-2397">
              Warranties regarding the accuracy, reliability, or completeness of
              recommendations
            </li>
            <li data-id="element-2398">Warranties that defects will be corrected</li>
          </ul>

          <h3 data-id="element-2399">10.2 AI Recommendations Disclaimer</h3>
          <p data-id="element-2400">
            AI-generated recommendations are based on algorithms and data
            analysis. They are provided for informational purposes only and
            should not be considered professional advice. We do not guarantee:
          </p>
          <ul data-id="element-2401">
            <li data-id="element-2402">The accuracy or effectiveness of recommendations</li>
            <li data-id="element-2403">That implementing recommendations will improve your results</li>
            <li data-id="element-2404">
              That recommendations are suitable for your specific business needs
            </li>
          </ul>
          <p data-id="element-2405">
            You are solely responsible for evaluating and implementing
            recommendations. We recommend testing changes before full
            implementation.
          </p>

          <h3 data-id="element-2406">10.3 No Professional Advice</h3>
          <p data-id="element-2407">
            The Service does not provide legal, financial, marketing, or other
            professional advice. You should consult with qualified professionals
            before making business decisions based on Service recommendations.
          </p>

          <h2 data-id="element-2408">11. Limitation of Liability</h2>

          <h3 data-id="element-2409">11.1 Limitation of Damages</h3>
          <p data-id="element-2410">
            TO THE MAXIMUM EXTENT PERMITTED BY LAW, ACCIOM LIMITED, ITS
            OFFICERS, DIRECTORS, EMPLOYEES, AGENTS, AND AFFILIATES SHALL NOT BE
            LIABLE FOR:
          </p>
          <ul data-id="element-2411">
            <li data-id="element-2412">
              Any indirect, incidental, special, consequential, or punitive
              damages
            </li>
            <li data-id="element-2413">Loss of profits, revenue, data, or business opportunities</li>
            <li data-id="element-2414">Loss of goodwill or reputation</li>
            <li data-id="element-2415">Service interruptions or data loss</li>
            <li data-id="element-2416">Errors or inaccuracies in recommendations</li>
            <li data-id="element-2417">Third-party actions or content</li>
          </ul>
          <p data-id="element-2418">
            This limitation applies regardless of the legal theory (contract,
            tort, negligence, strict liability, or otherwise) and even if we
            have been advised of the possibility of such damages.
          </p>

          <h3 data-id="element-2419">11.2 Maximum Liability</h3>
          <p data-id="element-2420">
            Our total liability to you for all claims arising from or related to
            the Service shall not exceed the greater of:
          </p>
          <ul data-id="element-2421">
            <li data-id="element-2422">
              The amount you paid us in the 12 months preceding the claim, or
            </li>
            <li data-id="element-2423">£100 (one hundred pounds sterling)</li>
          </ul>

          <h3 data-id="element-2424">11.3 Exceptions</h3>
          <p data-id="element-2425">
            Some jurisdictions do not allow the exclusion or limitation of
            certain warranties or damages. In such jurisdictions, our liability
            will be limited to the maximum extent permitted by law.
          </p>

          <h2 data-id="element-2426">12. Indemnification</h2>
          <p data-id="element-2427">
            You agree to indemnify, defend, and hold harmless Acciom Limited,
            its officers, directors, employees, agents, and affiliates from and
            against any claims, liabilities, damages, losses, costs, or expenses
            (including reasonable attorneys' fees) arising from or related to:
          </p>
          <ul data-id="element-2428">
            <li data-id="element-2429">Your use of the Service</li>
            <li data-id="element-2430">Your violation of these Terms</li>
            <li data-id="element-2431">Your violation of any third-party rights</li>
            <li data-id="element-2432">Your content or data</li>
            <li data-id="element-2433">Your implementation of recommendations</li>
          </ul>

          <h2 data-id="element-2434">13. Termination</h2>

          <h3 data-id="element-2435">13.1 Termination by You</h3>
          <p data-id="element-2436">You may terminate your account at any time by:</p>
          <ul data-id="element-2437">
            <li data-id="element-2438">
              Using the account deletion feature in your account settings, or
            </li>
            <li data-id="element-2439">
              Contacting us at{' '}
              <a href="mailto:support@useflowsense.com" data-id="element-2440">
                support@useflowsense.com
              </a>
            </li>
          </ul>
          <p data-id="element-2441">
            Upon termination, your access to the Service will cease immediately,
            and your data will be deleted in accordance with our Privacy Policy
            (within 30 days).
          </p>

          <h3 data-id="element-2442">13.2 Termination by Us</h3>
          <p data-id="element-2443">
            We may suspend or terminate your account immediately, without prior
            notice, if:
          </p>
          <ul data-id="element-2444">
            <li data-id="element-2445">You violate these Terms or our Acceptable Use Policy</li>
            <li data-id="element-2446">Your payment method fails repeatedly</li>
            <li data-id="element-2447">We are required to do so by law</li>
            <li data-id="element-2448">We discontinue the Service</li>
            <li data-id="element-2449">We reasonably believe your account poses a security risk</li>
          </ul>

          <h3 data-id="element-2450">13.3 Effect of Termination</h3>
          <p data-id="element-2451">Upon termination:</p>
          <ul data-id="element-2452">
            <li data-id="element-2453">Your right to use the Service immediately ceases</li>
            <li data-id="element-2454">
              You remain liable for all fees incurred prior to termination
            </li>
            <li data-id="element-2455">No refunds will be provided for unused subscription time</li>
            <li data-id="element-2456">
              Sections of these Terms that by their nature should survive
              termination will survive (including Intellectual Property,
              Limitation of Liability, and Indemnification)
            </li>
          </ul>

          <h2 data-id="element-2457">14. Dispute Resolution</h2>

          <h3 data-id="element-2458">14.1 Informal Resolution</h3>
          <p data-id="element-2459">
            Before filing a formal claim, you agree to contact us at{' '}
            <a href="mailto:support@useflowsense.com" data-id="element-2460">
              support@useflowsense.com
            </a>{' '}
            to attempt to resolve the dispute informally. We will work in good
            faith to resolve disputes within 30 days.
          </p>

          <h3 data-id="element-2461">14.2 Governing Law</h3>
          <p data-id="element-2462">
            These Terms are governed by and construed in accordance with the
            laws of England and Wales, without regard to conflict of law
            principles.
          </p>

          <h3 data-id="element-2463">14.3 Jurisdiction</h3>
          <p data-id="element-2464">
            Any disputes arising from or related to these Terms or the Service
            shall be subject to the exclusive jurisdiction of the courts of
            England and Wales.
          </p>

          <h3 data-id="element-2465">14.4 Arbitration (For Non-EU/UK Users)</h3>
          <p data-id="element-2466">
            For users outside the EU and UK, any dispute that cannot be resolved
            informally shall be resolved through binding arbitration in
            accordance with the rules of the London Court of International
            Arbitration (LCIA). The arbitration shall be conducted in English in
            London, UK.
          </p>

          <h3 data-id="element-2467">14.5 Class Action Waiver</h3>
          <p data-id="element-2468">
            You agree that any dispute resolution proceedings will be conducted
            only on an individual basis and not in a class, consolidated, or
            representative action. You waive your right to participate in a
            class action lawsuit or class-wide arbitration.
          </p>

          <h2 data-id="element-2469">15. General Provisions</h2>

          <h3 data-id="element-2470">15.1 Entire Agreement</h3>
          <p data-id="element-2471">
            These Terms, together with our Privacy Policy and EULA, constitute
            the entire agreement between you and Acciom Limited regarding the
            Service and supersede all prior agreements and understandings.
          </p>

          <h3 data-id="element-2472">15.2 Severability</h3>
          <p data-id="element-2473">
            If any provision of these Terms is found to be invalid or
            unenforceable, the remaining provisions will remain in full force
            and effect.
          </p>

          <h3 data-id="element-2474">15.3 Waiver</h3>
          <p data-id="element-2475">
            Our failure to enforce any right or provision of these Terms will
            not be deemed a waiver of such right or provision.
          </p>

          <h3 data-id="element-2476">15.4 Assignment</h3>
          <p data-id="element-2477">
            You may not assign or transfer these Terms or your account without
            our prior written consent. We may assign these Terms without
            restriction.
          </p>

          <h3 data-id="element-2478">15.5 Force Majeure</h3>
          <p data-id="element-2479">
            We will not be liable for any failure or delay in performance due to
            circumstances beyond our reasonable control, including acts of God,
            war, terrorism, riots, embargoes, acts of civil or military
            authorities, fire, floods, accidents, pandemics, strikes, or
            shortages of transportation, facilities, fuel, energy, labor, or
            materials.
          </p>

          <h3 data-id="element-2480">15.6 Export Compliance</h3>
          <p data-id="element-2481">
            You agree to comply with all applicable export and import laws and
            regulations. You represent that you are not located in a country
            subject to a U.S. or UK government embargo or designated as a
            "terrorist supporting" country.
          </p>

          <h3 data-id="element-2482">15.7 Language</h3>
          <p data-id="element-2483">
            These Terms are written in English. Any translations are provided
            for convenience only. In the event of a conflict, the English
            version shall prevail.
          </p>

          <h2 data-id="element-2484">16. Contact Information</h2>
          <p data-id="element-2485">If you have questions about these Terms, please contact us:</p>
          <div className="bg-gray-50 p-6 rounded-lg not-prose" data-id="element-2486">
            <p className="font-semibold text-gray-900 mb-2" data-id="element-2487">
              Flowsense by Acciom Limited
            </p>
            <p className="text-gray-700 text-sm mb-1" data-id="element-2488">
              <strong data-id="element-2489">General Inquiries:</strong>{' '}
              <a href="mailto:support@useflowsense.com" className="text-indigo-600 hover:underline" data-id="element-2490">
                support@useflowsense.com
              </a>
            </p>
            <p className="text-gray-700 text-sm" data-id="element-2491">
              <strong data-id="element-2492">Support:</strong>{' '}
              <a href="mailto:support@useflowsense.com" className="text-indigo-600 hover:underline" data-id="element-2493">
                support@useflowsense.com
              </a>
            </p>
          </div>

          <div className="bg-indigo-50 border-l-4 border-indigo-500 p-6 mt-8" data-id="element-2494">
            <p className="text-sm text-indigo-900 mb-2" data-id="element-2495">
              <strong data-id="element-2496">Questions About These Terms?</strong>
            </p>
            <p className="text-sm text-indigo-800 mb-0" data-id="element-2497">
              We want you to understand your rights and obligations. If anything
              in these Terms is unclear, please reach out to us. We're happy to
              explain.
            </p>
          </div>
        </div>
      </Container>
    </div>;
}